/* eslint-disable max-lines */
import { queryOptions } from '@tanstack/react-query';

import { fetchBackend } from './apiClient';

type UpdateTrigger =
  | {
      type: 'commit';
      commits: {
        author: string;
        sha: string;
        githubUrl: string;
        repository: string;
      }[];
    }
  | {
      type: 'cloud' | 'state';
    };

type PropertyChanges = Record<
  string,
  {
    before: string | undefined;
    after: string | undefined;
  }
>;

type ImpactChanges = {
  deleted: {
    [key: string]: {
      hashedID: string;
      anyshiftID: string;
      type: string;
    };
  };
  created: {
    [key: string]: {
      hashedID: string;
      anyshiftID: string;
      type: string;
    };
  };
};

export type ParsedHistoryItem = {
  timestamp: string;
  isCurrent: boolean;
  trigger: UpdateTrigger;
  propertyChanges: PropertyChanges;
  downstreamImpactChanges: ImpactChanges;
  upstreamImpactChanges: ImpactChanges;
  created: boolean;
  deleted: boolean;
};

export const resourceQueries = {
  all: () => ['resource'],
  byProject: (projectId: string) => [...resourceQueries.all(), projectId],
  details: (projectId: string) => [
    ...resourceQueries.byProject(projectId),
    'details',
  ],
  detail: <TResource extends AnyResource>(
    projectId: string,
    resourceId: string | undefined,
  ) =>
    queryOptions({
      queryKey: [...resourceQueries.details(projectId), resourceId],
      queryFn: async () => {
        if (resourceId === undefined) {
          return null;
        }

        const resource = (await getResource(
          projectId,
          `${resourceId}`,
        )) as TResource;

        return { resource };
      },
    }),
  history: (projectId: string, resourceId: string) =>
    queryOptions({
      queryKey: [
        ...resourceQueries.detail(projectId, resourceId).queryKey,
        'history',
      ],
      queryFn: async () => {
        const response = await fetchBackend(
          `/resource/${projectId}/${resourceId}/history`,
          {
            method: 'GET',
          },
        );

        if (!response.ok) {
          throw new Error(`Error: ${response.status}`);
        }

        return (await response.json()) as ParsedHistoryItem[];
      },
    }),
  impacts: (
    projectId: string,
    resourceId: string,
    from: string | undefined,
    to: string | undefined,
  ) =>
    queryOptions({
      queryKey: [
        ...resourceQueries.detail(projectId, resourceId).queryKey,
        'impacts',
        from,
        to,
      ],
      queryFn: async () => {
        if (from === undefined && to === undefined) {
          return { data: undefined };
        }

        const params = new URLSearchParams();
        if (from !== undefined) {
          params.set('from', from);
        }
        if (to !== undefined) {
          params.set('to', to);
        }
        const response = await fetchBackend(
          `/resource/${projectId}/${resourceId}/impacts?${params.toString()}`,
          {
            method: 'GET',
          },
        );

        if (!response.ok) {
          throw new Error(`Error: ${response.status}`);
        }

        const result = (await response.json()) as ResourceImpacts;

        return { data: result };
      },
    }),
} as const;

export const getResource = async (
  projectId: string,
  resourceId: string,
): Promise<AnyResource> => {
  const response = await fetchBackend(`/resource/${projectId}/${resourceId}`, {
    method: 'GET',
  });

  if (!response.ok) {
    throw new Error(`Error: ${response.status}`);
  }

  return (await response.json()) as AnyResource;
};

export type CloudResource = {
  type: 'CLOUD_RESOURCE';
  resourceType: string;
  resourceId: string;
  scannedInCloud: boolean;
  awsURL: string;
  label: string;
  region?: string;
  regionHashedID?: string;
  account?: string;
  accountHashedID?: string;
  service: string;
  serviceHashedID?: string;
  codeDefinitionName?: string;
  codeGithubURL?: string;
  moduleName?: string;
  moduleGithubURL?: string;
  hashedID: string;
  createdAt?: string; // ISOString
  updatedAt?: string; // ISOString
  codeLastUpdatedAt?: string; // ISOString
  codeCreatedAt?: string; // ISOString
  codeLastUpdatedURL?: string;
  codeCreatedURL?: string;
  updateCommitter?: string;
  createCommitter?: string;
  numberOfUpdates?: number;
  deletedAt?: string;
};

export type HostResource = {
  type: 'HOST';
  hostType: string;
  hostName: string;
  resources: HostedResource[];
};

export type HostedResource = {
  service: string;
  resourceType: string;
  resourceId: string;
  hashedID: string;
};

export type AnyResource = CloudResource | HostResource;

export type ResourceHistoryItem = {
  edges: {
    anyshiftID: string;
    hashedID: string;
    type: string;
  }[];
  edgesInverse: {
    anyshiftID: string;
    hashedID: string;
    type: string;
  }[];
  props: Record<string, string | number | boolean>;
  t: { timestamp: string; source: string[] };
  deleted: boolean;
};

export type ResourceHistory = {
  cloudHistory: ResourceHistoryItem[];
  stateHistory: ResourceHistoryItem[];
  tfHistory: ResourceHistoryItem[];
};

export type ResourceImpactItem = {
  anyshiftID: string;
  hashedID: string;
  timestamps: string[];
};

export type ResourceImpacts = {
  upstream: Record<string, ResourceImpactItem>;
  downstream: Record<string, ResourceImpactItem>;
};
